import React from "react";
// TODO: FIX FRONT IS DEPENDENT ON API TYPES

import type {
  Channel,
  ServerMessage,
} from "../../../../service-diagnostics-core/src/websocket/Message";
import useDistinctShape from "@ui/lib/hooks/useDistinctShape";
import { indexBy } from "diagnostics-lib/iterable";
import { useChannel } from "./useChannel";

/* @fixme Reworking database schema using natural key lead to inconsistency in the front-end as we only index content by ID, leading to false duplicate */
export const mergeItems = <T extends Item>(
  oldItems: T[],
  newItems: T[],
  removedIds: string[],
): T[] => {
  if (!newItems.length && !removedIds.length) return oldItems;
  const idIndexedItems = indexBy(oldItems, "id");
  for (const id of removedIds) idIndexedItems.delete(id);
  for (const i of newItems) {
    const oldItem = idIndexedItems.get(i.id);
    if (!oldItem || oldItem.updatedAt < i.updatedAt) idIndexedItems.set(i.id, i);
  }

  return Array.from(idIndexedItems.values());
};

async function updateItems<T extends Item>(
  channel: Channel,
  updatedAt: number,
  inflight: React.MutableRefObject<boolean>,
  pendingUpdatedAt: React.MutableRefObject<number | null>,
  fetchUpdatedItems: (since: string) => Promise<readonly [newItems: T[], deletedIds: string[]]>,
  mutateItems: (fn: (oldItems: T[]) => T[]) => void,
): Promise<void> {
  if (inflight.current) {
    pendingUpdatedAt.current =
      pendingUpdatedAt.current && pendingUpdatedAt.current < updatedAt
        ? pendingUpdatedAt.current
        : updatedAt;
    return;
  }
  inflight.current = true;

  let newItems: T[];
  let deletedItemIds: string[];
  try {
    [newItems, deletedItemIds] = await fetchUpdatedItems(new Date(updatedAt).toISOString());
    console.debug(
      `ws:fetched:${channel.key}:new_${newItems.length}:old_${deletedItemIds.length}`,
      new Date(updatedAt).toISOString(),
    );
    if (newItems.length || deletedItemIds.length)
      mutateItems(oldItems => mergeItems(oldItems, newItems, deletedItemIds));
  } finally {
    // sleep 2 seconds to avoid spamming the server
    await new Promise(resolve => window.setTimeout(resolve, 2_000));

    inflight.current = false;
  }

  if (pendingUpdatedAt.current) {
    const updatedAt = pendingUpdatedAt.current;
    pendingUpdatedAt.current = null;
    void updateItems(
      channel,
      updatedAt,
      inflight,
      pendingUpdatedAt,
      fetchUpdatedItems,
      mutateItems,
    );
  }
}

const useOnChannelUpdate = <T extends Item>(
  channel: Channel,
  { readItems, fetchUpdatedItems, mutateItems }: UpdateOptions<T>,
): ((message: { updatedAt: string | null; initial: boolean }) => void) => {
  const inflight = React.useRef(false);
  const pendingUpdatedAt = React.useRef<number>(null);
  const stableChannel = useDistinctShape(channel);

  return React.useCallback(
    (message: { updatedAt: string | null; initial: boolean }) => {
      if (message.updatedAt === null) return; // collection is empty
      const remoteUpdatedAt = new Date(message.updatedAt).getTime();
      const initial = message.initial;
      const items = readItems();
      if (items === undefined) return; // collection is not yet loaded
      const localUpdatedAt =
        items.reduce<number | null>((acc, item) => {
          const itemUpdatedAt = new Date(item.updatedAt).getTime();
          if (!acc) return itemUpdatedAt;
          return acc > itemUpdatedAt ? acc : itemUpdatedAt;
        }, null) ?? new Date(0).getTime();

      // BEWARE
      // localUpdatedAt can be AFTER remoteUpdatedAt!
      // This happens when transactions unfold in random order : updatedAt is the beginning of the transaction, not the end
      // if initial and equal, we do not bother (we assume no transaction started before localUpdatedAt unfolded between initial fetch and WS connection)
      if (initial && localUpdatedAt === remoteUpdatedAt) return; // do not fetch items if they are already up to date on startup
      // we assume no transaction can start at exactly the same time and avoid requesting current items
      // and as stated before, we may need to request items that are before localUpdatedAt
      const updatedAt = Math.min(remoteUpdatedAt, localUpdatedAt + 1);

      void updateItems(
        stableChannel,
        updatedAt,
        inflight,
        pendingUpdatedAt,
        fetchUpdatedItems,
        mutateItems,
      );
    },
    [fetchUpdatedItems, mutateItems, readItems, stableChannel],
  );
};

type UpdateChannel = {
  [K in Channel as K["key"]]: ServerMessage<K>["payload"] extends {
    updatedAt: string | null;
    initial: boolean;
  }
    ? K
    : never;
}[Channel["key"]];

interface Item {
  id: string;
  updatedAt: string;
}

interface UpdateOptions<T extends Item> {
  readItems: () => T[] | undefined;
  fetchUpdatedItems: (since: string) => Promise<readonly [newItems: T[], deletedIds: string[]]>;
  mutateItems: (fn: (oldItems: T[]) => T[]) => void;
}

// high level hook to orchestrate the updates of a collection of "Item"s through:
// - functions provided in "UpdateOptions" to fetch and mutate the collection of "Item"s
// - a WS "UpdateChannel" that continously sends an "updatedAt" value representing the last modification made to the collection on the backend
export const useChannelUpdates = <T extends Item>(
  channel: UpdateChannel,
  updateOptions: UpdateOptions<T>,
  doIt?: boolean,
): void => useChannel(channel, useOnChannelUpdate(channel, updateOptions), true, doIt);
